import { motion } from 'framer-motion';
import { Calendar, User, Clock, Search, Filter, X, Tag, TrendingUp, ThumbsUp, Eye } from 'lucide-react';

const blogPosts = [
  {
    id: 1,
    title: 'The Future of AI in Digital Marketing',
    excerpt: 'Explore how artificial intelligence is revolutionizing the digital marketing landscape and what it means for businesses in 2025 and beyond. Learn about the latest trends, tools, and strategies.',
    image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=800&h=500',
    author: {
      name: 'Sarah Johnson',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&h=150'
    },
    date: '2025-03-15',
    readTime: '5 min read',
    category: 'AI & Technology',
    tags: ['Artificial Intelligence', 'Marketing', 'Technology'],
    stats: {
      views: '2.5k',
      likes: '180'
    }
  },
  {
    id: 2,
    title: 'Maximizing ROI with Data-Driven Strategies',
    excerpt: 'Learn how to leverage data analytics to optimize your marketing campaigns and achieve better returns on investment. Discover practical tips and real-world case studies.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&h=500',
    author: {
      name: 'Michael Chen',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150'
    },
    date: '2025-03-12',
    readTime: '7 min read',
    category: 'Marketing Strategy',
    tags: ['Data Analytics', 'ROI', 'Marketing'],
    stats: {
      views: '1.8k',
      likes: '145'
    }
  },
  {
    id: 3,
    title: 'Building a Strong Digital Brand Presence',
    excerpt: 'Discover the key elements of creating and maintaining a powerful digital brand that resonates with your audience. From visual identity to content strategy.',
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=800&h=500',
    author: {
      name: 'Emily Rodriguez',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&h=150'
    },
    date: '2025-03-10',
    readTime: '6 min read',
    category: 'Branding',
    tags: ['Branding', 'Digital Marketing', 'Design'],
    stats: {
      views: '3.2k',
      likes: '210'
    }
  },
  {
    id: 4,
    title: 'The Rise of Voice Search Optimization',
    excerpt: 'Understanding the growing importance of voice search and how to optimize your digital content for this emerging technology. Stay ahead of the curve.',
    image: 'https://images.unsplash.com/photo-1519558260268-cde7e03a0152?auto=format&fit=crop&w=800&h=500',
    author: {
      name: 'Alex Thompson',
      avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=150&h=150'
    },
    date: '2025-03-08',
    readTime: '8 min read',
    category: 'SEO',
    tags: ['Voice Search', 'SEO', 'Technology'],
    stats: {
      views: '1.5k',
      likes: '98'
    }
  }
];

const popularTags = [
  'Marketing',
  'Technology',
  'AI',
  'Branding',
  'Social Media',
  'Analytics',
  'SEO',
  'Content'
];

export default function Blog() {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <header className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-800 text-white py-24">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center opacity-10" />
          <div className="absolute inset-0 bg-blue-900/50" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Latest Insights & Trends
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-blue-100">
              Expert perspectives on digital marketing, technology, and business growth
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:w-2/3">
            {/* Featured Post */}
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden mb-12"
            >
              <div className="relative h-96">
                <img
                  src={blogPosts[0].image}
                  alt={blogPosts[0].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <span className="inline-block px-3 py-1 rounded-full bg-blue-500 text-sm font-medium mb-4">
                    Featured
                  </span>
                  <h2 className="text-3xl font-bold mb-4">{blogPosts[0].title}</h2>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <img
                        src={blogPosts[0].author.avatar}
                        alt={blogPosts[0].author.name}
                        className="w-8 h-8 rounded-full"
                      />
                      <span>{blogPosts[0].author.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(blogPosts[0].date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{blogPosts[0].readTime}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.article>

            {/* Recent Posts Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {blogPosts.slice(1).map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="relative h-48">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="inline-block px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm text-sm font-medium text-blue-600">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="inline-block px-2 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img
                          src={post.author.avatar}
                          alt={post.author.name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">{post.author.name}</p>
                          <p className="text-gray-500">{post.readTime}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-gray-500 text-sm">
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{post.stats.views}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ThumbsUp className="w-4 h-4" />
                          <span>{post.stats.likes}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3 space-y-8">
            {/* Popular Tags */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Tags</h3>
              <div className="flex flex-wrap gap-2">
                {popularTags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-block px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-medium hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors duration-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Trending Posts */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Trending Posts</h3>
              </div>
              <div className="space-y-4">
                {blogPosts.slice(0, 3).map((post, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <span className="text-2xl font-bold text-blue-600">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h4 className="font-medium text-gray-900 line-clamp-2">{post.title}</h4>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        <Eye className="w-4 h-4" />
                        <span>{post.stats.views} views</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}